<div class="relative z-40 bg-brand-surface-card">
  <!-- Main Header -->
  <div class="w-full h-16 flex items-center justify-between px-4 sm:px-6 lg:px-16 text-base text-brand-ink-secondary">
    <!-- Left: Logo and Desktop Nav -->
    <div class="flex items-center gap-4 lg:gap-7">
      <a routerLink="/home" class="flex items-center gap-2 text-lg sm:text-xl font-semibold text-brand-ink">
        <img src="assets/icons/sidebar/ApoloLogo.svg" alt="Signly" class="w-8 h-8 sm:w-9 sm:h-9" />
        <span class="hidden sm:inline">Signly</span>
      </a>

      <!-- Desktop Navigation -->
      <nav class="hidden lg:flex items-center gap-4 text-[15px]">
        <a
          *ngFor="let link of visibleNavLinks"
          [routerLink]="link.path"
          routerLinkActive="text-brand-primary-medium font-semibold"
          #rla="routerLinkActive"
          class="relative px-4 py-2 rounded-full transition hover:text-brand-ink hover:bg-brand-surface-raised hover:shadow-sm"
        >
          {{ link.label | translate }}
          <span
            class="absolute left-0 -bottom-0.5 h-0.5 w-full bg-brand-primary-medium rounded-full transition opacity-0"
            [ngClass]="{ 'opacity-100': rla.isActive }"
          ></span>
        </a>
      </nav>
    </div>

    <!-- Right: Actions -->
    <div class="flex items-center gap-2 sm:gap-3 lg:gap-5">
      <!-- Theme Toggle -->
      <app-theme-toggle class="hidden sm:block"></app-theme-toggle>

      <!-- Root user flag -->
      <div *ngIf="userProfile.isRoot"
           class="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700"
           [attr.title]="'HEADER.ROOT_USER_TOOLTIP' | translate"
           [attr.aria-label]="'HEADER.ROOT_USER_TOOLTIP' | translate">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M5 3a1 1 0 0 1 1 1v1.382l1.447-.724a3 3 0 0 1 2.683 0l1.34.67a1 1 0 0 0 .894 0l1.34-.67a3 3 0 0 1 2.683 0L18 5.382V4a1 1 0 1 1 2 0v16a1 1 0 1 1-2 0v-7.382l-1.447.724a3 3 0 0 1-2.683 0l-1.34-.67a1 1 0 0 0-.894 0l-1.34.67a3 3 0 0 1-2.683 0L6 12.618V20a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1z"/>
        </svg>
        <span>{{ 'HEADER.ROOT_USER_BADGE' | translate }}</span>
      </div>

      <!-- User Profile -->
      <div class="relative">
        <button
          type="button"
          (click)="toggleUserMenu($event)"
          class="flex items-center gap-2 sm:gap-3 rounded-full border border-brand bg-brand-surface-card px-2 sm:px-3 py-1.5 transition hover:border-brand-primary-medium"
          [attr.aria-expanded]="isUserMenuOpen"
        >
          <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-brand-avatar flex items-center justify-center text-brand-ink font-semibold text-sm">
            {{ userInitials }}
          </div>
          <div class="hidden md:flex flex-col leading-tight text-left">
            <span class="text-xs text-brand-muted-light uppercase tracking-wide">{{ 'HEADER.ACCOUNT' | translate }}</span>
            <span class="text-sm font-semibold text-brand-ink truncate max-w-[120px]">{{ dropdownDisplayName }}</span>
          </div>
          <svg
            class="w-4 h-4 text-brand-muted transition-transform duration-200"
            [ngClass]="isUserMenuOpen ? 'rotate-180' : ''"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <app-user-profile-dropdown
          *ngIf="isUserMenuOpen"
          class="absolute right-0 mt-3 z-50"
          [userName]="dropdownUserName"
          [userEmail]="dropdownUserEmail"
          [accountInfo]="dropdownAccountInfo"
          [displayName]="dropdownDisplayName"
          (logOut)="handleLogout()"
          (manageProfile)="handleManageProfile()"
        ></app-user-profile-dropdown>
      </div>

      <!-- Mobile Menu Button -->
      <button
        type="button"
        (click)="toggleMobileMenu()"
        class="lg:hidden flex items-center justify-center rounded-full border border-brand bg-brand-surface-card p-2 transition hover:border-brand-primary-medium"
        [attr.aria-expanded]="isMobileMenuOpen"
        aria-label="Menu"
      >
        <svg *ngIf="!isMobileMenuOpen" class="w-6 h-6 text-brand-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <svg *ngIf="isMobileMenuOpen" class="w-6 h-6 text-brand-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Mobile Menu -->
  <div
    *ngIf="isMobileMenuOpen"
    class="lg:hidden border-t border-brand-subtle bg-brand-surface-card shadow-lg"
  >
    <!-- Mobile Navigation Links -->
    <nav class="px-4 py-3 space-y-1">
      <a
        *ngFor="let link of visibleNavLinks"
        [routerLink]="link.path"
        routerLinkActive="bg-brand-surface-raised text-brand-primary-medium font-semibold"
        (click)="closeMenus()"
        class="block px-4 py-3 rounded-xl text-sm text-brand-ink hover:bg-brand-surface-raised transition"
      >
        {{ link.label | translate }}
      </a>
    </nav>

    <!-- Mobile Actions -->
    <div class="px-4 py-3 border-t border-brand-subtle space-y-3">
      <!-- Theme toggle for Mobile -->
      <div class="flex items-center gap-3 sm:hidden">
        <app-theme-toggle class="flex-1"></app-theme-toggle>
      </div>
    </div>
  </div>
</div>
